import { useEffect, useState } from 'react';
import { usePortfolios } from './use-portfolios';

// PROGRESSIVE DISCLOSURE FOR PORTFOLIOS.
//
// A portfolio is a way to GROUP buildings, so it is meaningless to an admin who
// has one. Onboarding no longer mentions it (WP-17) and this keeps the promise
// on the dashboard: the nav item stays out of the way until the second building
// exists, and appears once with a hint explaining what just became available.
//
// Nothing is removed and nothing is gated. The screens, the routes, the picker
// and the data model are all untouched — an admin who types /dashboard/portfolios
// still gets the screen, and the server is unchanged. This is visibility only,
// which is the whole point: hiding a feature the customer cannot use yet is not
// the same as taking it away.
//
// THE COUNT COMES FROM THE PORTFOLIOS PAYLOAD, not a second request. Every
// portfolio already carries building_count (hierarchy-interfaces.ts:103), and
// the nav item is already gated on portfolio:read — so anyone who could see the
// item can already make this call, and nobody makes an extra one. A concierge
// holds building:read but not portfolio:read, so usePortfolios settles
// immediately with allowed=false and this returns "do not show", which is also
// the correct answer for them.

const HINT_KEY = 'lobbero.portfolioHintSeen';

// The threshold is 2 because that is when grouping starts to mean something.
// With one building a portfolio is a container with one thing in it.
const REVEAL_AT = 2;

const hintWasSeen = (): boolean => {
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    // Private mode: treat as unseen. Showing a subtle hint twice is a much
    // smaller cost than suppressing it for someone who never saw it.
    return false;
  }
};

export interface PortfolioDisclosure {
  // Whether the Portfolios nav item should render at all.
  showPortfolios: boolean;
  // Whether to show the one-time "you can group buildings now" hint.
  showHint: boolean;
  dismissHint: () => void;
  buildingCount: number;
  isLoading: boolean;
}

export const usePortfolioDisclosure = (): PortfolioDisclosure => {
  const { portfolios, isLoading, allowed } = usePortfolios();
  const [dismissed, setDismissed] = useState<boolean>(hintWasSeen);

  const buildingCount = portfolios.reduce(
    (total, portfolio) => total + (portfolio.building_count || 0),
    0
  );

  // More than one PORTFOLIO already existing also reveals it, regardless of the
  // building count: an admin who has grouped things before must not have the
  // navigation to their own data taken away by this rule.
  const showPortfolios =
    allowed && (buildingCount >= REVEAL_AT || portfolios.length > 1);

  // Only while it is still fresh. Once the admin has several buildings and has
  // clearly found the feature, an unread hint is just clutter — but we cannot
  // know that, so the localStorage flag is the only suppressor.
  const showHint = showPortfolios && !dismissed && !isLoading;

  // Written as soon as it is shown rather than only on click: a hint the user
  // scrolled past and ignored has still been seen, and re-showing it on every
  // page load is the behaviour that makes people distrust a UI.
  useEffect(() => {
    if (!showHint) return;

    try {
      window.localStorage.setItem(HINT_KEY, '1');
    } catch {
      /* private mode — session-only suppression is fine */
    }
  }, [showHint]);

  const dismissHint = () => setDismissed(true);

  return { showPortfolios, showHint, dismissHint, buildingCount, isLoading };
};
